import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  ChartOfAccountStyles,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";
import { capitalize } from "./CostCenterForm";

// The API sends "-" for an unset parent, department or customer.
const isBlank = (value) => !value || value === "-";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await Http.get(`/cost-center`);
      // budget_amount is formatted here as well as in the column body, so the
      // CSV/PDF export carries the same text the table shows.
      setReceiveData(
        (res.data || []).map((item) => ({
          ...item,
          budget_amount_display: money(item.budget_amount),
        })),
      );
    } catch (error) {
      console.error("Fetch error:", error);
      showAlert(
        "Could not load cost centers. Please refresh and try again.",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const deleteData = (item) => {
    Swal.fire({
      title: "Delete this cost center?",
      text: `"${item.code} - ${item.name}" will be removed permanently.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      Http.delete("/cost-center/" + item.id)
        .then((res) => {
          showAlert(res.data.message, res.data.type || "success");
          fetchData();
        })
        .catch((e) => {
          // A parent with children comes back as a 400 carrying the reason.
          Swal.fire(
            "Not deleted",
            e.response?.data?.message ||
              "Could not delete the cost center. Please try again.",
            "warning",
          );
        });
    });
  };

  // Counted over every record: the table's status filter starts on "active",
  // so these also say what that filter is hiding.
  const stats = useMemo(() => {
    const rows = receiveData || [];
    const active = rows.filter((row) => row.status === "active");
    return {
      total: rows.length,
      active: active.length,
      inactive: rows.length - active.length,
      topLevel: rows.filter((row) => !row.parent_id).length,
      budget: active.reduce((sum, row) => sum + num(row.budget_amount), 0),
      customers: new Set(
        rows.filter((row) => row.acc_customer_id).map((r) => r.acc_customer_id),
      ).size,
    };
  }, [receiveData]);

  const codeBodyTemplate = (rowData) => (
    <span className="coa-code">{rowData.code}</span>
  );

  const nameBodyTemplate = (rowData) => (
    <span style={{ fontWeight: 600, color: "#2a2962" }}>
      {rowData.name}
      {rowData.child_count > 0 && (
        <span className="coa-chip-count mg-l-8" title="Child cost centers">
          {rowData.child_count}
        </span>
      )}
    </span>
  );

  const typeBodyTemplate = (rowData) => (
    <span className="coa-pill coa-pill-dr">{capitalize(rowData.type)}</span>
  );

  const textBodyTemplate = (field) => {
    const Component = (rowData) =>
      isBlank(rowData[field]) ? (
        <span style={{ color: "#b6bacb" }}>—</span>
      ) : (
        <span>{rowData[field]}</span>
      );
    Component.displayName = `TextBodyTemplate(${field})`;
    return Component;
  };

  const budgetBodyTemplate = (rowData) => {
    const value = num(rowData.budget_amount);
    return (
      <span
        className="coa-num"
        style={{
          display: "block",
          fontWeight: value ? 600 : 400,
          color: value ? "#2a2962" : "#b6bacb",
        }}
      >
        {money(value)}
      </span>
    );
  };

  const cols = [
    { field: "code", header: "Code", body: codeBodyTemplate },
    { field: "name", header: "Name", body: nameBodyTemplate },
    { field: "type", header: "Type", body: typeBodyTemplate },
    { field: "parent", header: "Parent", body: textBodyTemplate("parent") },
    {
      field: "department",
      header: "Department",
      body: textBodyTemplate("department"),
    },
    {
      field: "customer",
      header: "Customer",
      body: textBodyTemplate("customer"),
    },
    {
      field: "budget_amount_display",
      sortField: "budget_amount",
      header: "Budget",
      body: budgetBodyTemplate,
      headerClassName: "coa-num",
    },
    // "Status" (exact) turns on ReactDataTable's active/inactive tag and filter.
    { field: "status", header: "Status" },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Cost Centers</h5>
            <p className="text-muted mb-0 tx-12">
              Where costs are tracked: departments, projects, branches and
              products
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-cost-center" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Cost Center
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-bullseye coa-stat-icon"></i>
                <span className="coa-stat-label">Cost Centers</span>
                <span className="coa-stat-value">{stats.total}</span>
                <span className="coa-stat-sub">{stats.topLevel} top-level</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat is-ok">
                <i className="fa fa-check-circle-o coa-stat-icon"></i>
                <span className="coa-stat-label">Active</span>
                <span className="coa-stat-value">{stats.active}</span>
                <span className="coa-stat-sub">
                  {stats.inactive > 0
                    ? `${stats.inactive} inactive, hidden by the status filter`
                    : "None inactive"}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-money coa-stat-icon"></i>
                <span className="coa-stat-label">Active Budget</span>
                <span className="coa-stat-value">{money(stats.budget)}</span>
                <span className="coa-stat-sub">Across active cost centers</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-handshake-o coa-stat-icon"></i>
                <span className="coa-stat-label">Customers</span>
                <span className="coa-stat-value">{stats.customers}</span>
                <span className="coa-stat-sub">With a linked cost center</span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>All Cost Centers
              </span>
            </div>
            <div className="coa-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item, deleteData) => {
  const hasChildren = item.child_count > 0;

  return (
    <div className="d-flex gap-1">
      <Link
        to={{ pathname: "/edit-cost-center/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit cost center"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      {/* The server refuses to delete a parent, so the button says why up front. */}
      <span
        title={
          hasChildren
            ? "Move or delete its child cost centers first"
            : "Delete cost center"
        }
      >
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => deleteData(item)}
          disabled={hasChildren}
          style={hasChildren ? { pointerEvents: "none" } : undefined}
        >
          <i className="fa fa-trash" aria-hidden="true"></i>
        </button>
      </span>
    </div>
  );
};

export default List;
